import { supabase } from './supabase'
import { User } from '@supabase/supabase-js'

export interface UserProfile {
  id: string
  email: string
  full_name: string
  role: 'coach' | 'coachee' | 'admin'
  avatar_url: string
  profile_picture_url?: string
  profile_picture_visibility?: 'public' | 'private' | 'none'
  onboarding_completed: boolean
  show_motivational_messages: boolean
  created_at: string
  updated_at: string
}

export class UserService {
  /**
   * Get or create user profile
   * This function ensures a profile exists for the user
   */
  static async getOrCreateProfile(supabaseUser: User): Promise<UserProfile | null> {
    try {
      // First, try to fetch existing profile
      const { data: initialProfileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', supabaseUser.id)
        .single()

      let profileData = initialProfileData

      // If profile doesn't exist (PGRST116 error), create it
      if (profileError && profileError.code === 'PGRST116') {
        console.log('Profile not found, creating new profile for user:', supabaseUser.id)
        
        const newProfile = {
          id: supabaseUser.id,
          email: supabaseUser.email!,
          full_name: supabaseUser.user_metadata?.full_name || '',
          role: (supabaseUser.user_metadata?.role as 'coach' | 'coachee' | 'admin') || 'coachee',
          avatar_url: supabaseUser.user_metadata?.avatar_url || '',
        }

        const { data: createdProfile, error: createError } = await supabase
          .from('profiles')
          .insert([newProfile])
          .select()
          .single()

        if (createError) {
          console.error('Error creating profile:', createError)
          return null
        }

        profileData = createdProfile
      } else if (profileError) {
        console.error('Error fetching profile:', profileError)
        return null
      }

      if (profileData) {
        return {
          id: profileData.id,
          email: profileData.email,
          full_name: profileData.full_name || '',
          role: profileData.role || supabaseUser.user_metadata?.role || 'coachee',
          avatar_url: profileData.avatar_url || '',
          profile_picture_url: profileData.profile_picture_url || undefined,
          profile_picture_visibility: profileData.profile_picture_visibility || 'private',
          onboarding_completed: profileData.onboarding_completed || false,
          show_motivational_messages: profileData.show_motivational_messages !== false,
          created_at: supabaseUser.created_at,
          updated_at: profileData.updated_at || supabaseUser.updated_at || supabaseUser.created_at,
        }
      }

      return null
    } catch (error) {
      console.error('Unexpected error in getOrCreateProfile:', error)
      return null
    }
  }

  /**
   * Update user profile
   */
  static async updateProfile(userId: string, updates: Partial<Omit<UserProfile, 'id' | 'created_at' | 'updated_at'>>): Promise<UserProfile | null> {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', userId)
        .select()
        .single()

      if (error) {
        console.error('Error updating profile:', error)
        return null
      }

      return data
    } catch (error) {
      console.error('Unexpected error in updateProfile:', error)
      return null
    }
  }

  /**
   * Get user profile by ID
   */
  static async getProfile(userId: string): Promise<UserProfile | null> {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single()

      if (error) {
        console.error('Error fetching profile:', error)
        return null
      }

      return data
    } catch (error) {
      console.error('Unexpected error in getProfile:', error)
      return null
    }
  }

  /**
   * Create initial user preferences
   */
  static async createInitialPreferences(userId: string): Promise<void> {
    try {
      const defaultPreferences = {
        user_id: userId,
        theme_id: 'default',
        dark_mode: false,
        font_size: 'md',
        reduced_motion: false,
        high_contrast: false,
        compact_mode: false,
      }

      const { error } = await supabase
        .from('user_preferences')
        .insert([defaultPreferences])

      if (error && error.code !== '23505') { // Ignore duplicate key error
        console.error('Error creating initial preferences:', error)
      }
    } catch (error) {
      console.error('Unexpected error in createInitialPreferences:', error)
    }
  }

  /**
   * Promote a user to admin role
   * WARNING: This should only be used by existing admins or for initial setup
   */
  static async promoteToAdmin(userEmail: string, currentAdminId?: string): Promise<{ success: boolean; message: string }> {
    try {
      // Optional: Check if current user is admin (if currentAdminId is provided)
      if (currentAdminId) {
        const { data: currentUser } = await supabase
          .from('profiles')
          .select('role')
          .eq('id', currentAdminId)
          .single()

        if (!currentUser || currentUser.role !== 'admin') {
          return { success: false, message: 'Only admins can promote users to admin' }
        }
      }

      // Find user by email
      const { data: user, error: findError } = await supabase
        .from('profiles')
        .select('*')
        .eq('email', userEmail)
        .single()

      if (findError || !user) {
        return { success: false, message: 'User not found' }
      }

      if (user.role === 'admin') {
        return { success: false, message: 'User is already an admin' }
      }

      // Update user role to admin
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ role: 'admin' })
        .eq('email', userEmail)

      if (updateError) {
        console.error('Error promoting user to admin:', updateError)
        return { success: false, message: 'Failed to promote user to admin' }
      }

      return { success: true, message: `User ${userEmail} has been promoted to admin` }
    } catch (error) {
      console.error('Unexpected error in promoteToAdmin:', error)
      return { success: false, message: 'An unexpected error occurred' }
    }
  }

  /**
   * Get all admin users
   */
  static async getAdmins(): Promise<UserProfile[]> {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('role', 'admin')
        .order('created_at', { ascending: true })

      if (error) {
        console.error('Error fetching admins:', error)
        return []
      }

      return data || []
    } catch (error) {
      console.error('Unexpected error in getAdmins:', error)
      return []
    }
  }

  /**
   * Check if user is admin
   */
  static async isUserAdmin(userId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', userId)
        .single()

      if (error || !data) {
        return false
      }

      return data.role === 'admin'
    } catch (error) {
      console.error('Unexpected error in isUserAdmin:', error)
      return false
    }
  }
}

export default UserService 